/**
 * Event bus for visualization layer communication
 * Provides type-safe event handling between UI and visualization components
 */

import { TransactionEvent } from '../elements/VisualElementsManager';
import { logger } from '../../core/utils/Logger';

export type VisualizationEvent = 
  | { type: 'transaction'; data: TransactionEvent }
  | { type: 'network_stats'; data: { hashrate: number; difficulty: number } }
  | { type: 'block'; data: { height: number; size: number; timestamp: number } }
  | { type: 'system_pause'; data: {} }
  | { type: 'system_resume'; data: {} };

type EventHandler<T extends VisualizationEvent> = (event: T['data']) => void;

export class VisualizationEventBus {
  private handlers = new Map<string, Set<EventHandler<any>>>();

  public on<T extends VisualizationEvent>(
    eventType: T['type'], 
    handler: EventHandler<T>
  ): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    
    this.handlers.get(eventType)!.add(handler);
    
    // Return unsubscribe function
    return () => {
      const typeHandlers = this.handlers.get(eventType);
      if (typeHandlers) {
        typeHandlers.delete(handler);
        if (typeHandlers.size === 0) {
          this.handlers.delete(eventType);
        }
      }
    };
  }

  public emit<T extends VisualizationEvent>(eventType: T['type'], data: T['data']): void {
    const typeHandlers = this.handlers.get(eventType);
    if (typeHandlers) {
      typeHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          logger.error(`Error in visualization event handler for ${eventType}`, 'VisualizationEventBus', error as Error);
        }
      });
    }
  }

  public off(eventType: string, handler?: EventHandler<any>): void {
    if (!handler) {
      // Remove all handlers for this event type
      this.handlers.delete(eventType);
    } else {
      const typeHandlers = this.handlers.get(eventType);
      if (typeHandlers) {
        typeHandlers.delete(handler);
        if (typeHandlers.size === 0) {
          this.handlers.delete(eventType);
        }
      }
    }
  }

  public clear(): void {
    this.handlers.clear();
  }

  public getListenerCount(eventType?: string): number {
    if (eventType) {
      return this.handlers.get(eventType)?.size || 0;
    }
    
    let total = 0;
    for (const handlers of this.handlers.values()) {
      total += handlers.size;
    }
    return total;
  }
}

// Global visualization event bus instance
export const visualizationEventBus = new VisualizationEventBus();